import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  List,
  Progress,
  Row,
  Select,
  Tag,
  Typography,
  Upload
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { listImports, startImport } from "./api";
import { formatDateTime } from "./format";

const STATUS_COLORS = {
  queued: "default",
  running: "processing",
  done: "success",
  failed: "error"
};

/** DATANORM upload (files or a ZIP) and the import jobs with progress and their report. */
const ImportTab = ({ meta, onImported }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [supplier, setSupplier] = useState(meta.suppliers[0]?._id || null);
  const [encoding, setEncoding] = useState("auto");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [jobs, setJobs] = useState([]);
  const timer = useRef(null);

  const load = async () => {
    try {
      const res = await listImports();
      setJobs(res.data.data);
      const running = res.data.data.some(j => ["queued", "running"].includes(j.status));
      clearTimeout(timer.current);
      if (running) timer.current = setTimeout(load, 2000);
      return running;
    } catch (error) {
      generalErrorAlert(error);
      return false;
    }
  };

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, []);

  const start = async () => {
    setUploading(1);
    try {
      await startImport({ supplier, encoding, files }, e =>
        setUploading(Math.max(1, Math.round((e.loaded / (e.total || 1)) * 100)))
      );
      Success(t("The import has started."));
      setFiles([]);
      await load();
      onImported();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setUploading(0);
    }
  };

  const labels = {
    queued: t("Waiting"),
    running: t("Running"),
    done: t("Done"),
    failed: t("Failed")
  };
  const encodings = {
    auto: t("Detect automatically"),
    cp850: "DOS (CP850)",
    cp437: "DOS (CP437)",
    latin1: "ISO-8859-1 / Windows",
    utf8: "UTF-8"
  };

  return (
    <div>
      {meta.permissions.create ? (
        <Card className="mat-section" title={t("Import DATANORM files")}>
          {!meta.suppliers.length ? (
            <Alert
              type="info"
              showIcon
              message={t("Create the supplier first (tab Suppliers & discounts).")}
            />
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div className="mat-muted">{t("Supplier")}</div>
                <Select
                  className="mat-full"
                  value={supplier}
                  onChange={setSupplier}
                  options={meta.suppliers.map(s => ({ value: s._id, label: s.name }))}
                />
              </Col>
              <Col xs={24} md={12}>
                <div className="mat-muted">{t("Character set")}</div>
                <Select
                  className="mat-full"
                  value={encoding}
                  onChange={setEncoding}
                  options={meta.encodings.map(e => ({ value: e, label: encodings[e] || e }))}
                />
              </Col>
              <Col xs={24}>
                <Upload
                  multiple
                  fileList={files.map(f => ({ uid: f.uid, name: f.name, size: f.size }))}
                  beforeUpload={file => {
                    setFiles(prev => [...prev, file]);
                    return false;
                  }}
                  onRemove={file => setFiles(prev => prev.filter(f => f.uid !== file.uid))}
                >
                  <Button icon={<UploadOutlined />}>
                    {t("Choose files (DATANORM.001, DATPREIS, .RAB or ZIP)")}
                  </Button>
                </Upload>
                <Typography.Paragraph className="mat-muted mat-section">
                  {t(
                    "DATANORM 4 and 5 are supported. Articles are matched by supplier and article number: a new delivery updates them."
                  )}
                </Typography.Paragraph>
              </Col>
              <Col xs={24}>
                {uploading ? <Progress percent={uploading} size="small" /> : null}
                <Button
                  type="primary"
                  disabled={!files.length || !supplier}
                  loading={Boolean(uploading)}
                  onClick={start}
                >
                  {t("Start import")}
                </Button>
              </Col>
            </Row>
          )}
        </Card>
      ) : null}

      <Card className="mat-section" title={t("Imports")}>
        <List
          dataSource={jobs}
          locale={{ emptyText: <Empty description={t("No imports yet")} /> }}
          renderItem={job => {
            const percent = job.progress?.totalBytes
              ? Math.min(100, Math.round((job.progress.bytes / job.progress.totalBytes) * 100))
              : 0;
            const c = job.counts || {};
            return (
              <List.Item>
                <div className="mat-full">
                  <div>
                    <Tag color={STATUS_COLORS[job.status]}>{labels[job.status]}</Tag>
                    <strong>{job.supplier?.name}</strong>{" "}
                    <span className="mat-muted">
                      {formatDateTime(job.createdAt, i18n.language)} ·{" "}
                      {(job.files || []).map(f => f.name).join(", ")}
                      {job.version ? ` · DATANORM ${job.version}` : ""}
                      {job.detectedEncoding
                        ? ` · ${encodings[job.detectedEncoding] || job.detectedEncoding}`
                        : ""}
                    </span>
                  </div>
                  {job.status === "running" ? <Progress percent={percent} size="small" /> : null}
                  <div className="mat-muted">
                    {t(
                      "{{created}} new, {{updated}} updated, {{deactivated}} deactivated, {{prices}} prices, {{texts}} texts, {{discounts}} discount groups, {{errors}} errors",
                      {
                        created: c.created || 0,
                        updated: c.updated || 0,
                        deactivated: c.deactivated || 0,
                        prices: c.prices || 0,
                        texts: c.texts || 0,
                        discounts: c.discounts || 0,
                        errors: c.errors || 0
                      }
                    )}
                    {c.pricesUnknown
                      ? ` · ${t("{{count}} prices for unknown articles", { count: c.pricesUnknown })}`
                      : ""}
                  </div>
                  {job.message ? <Alert type="error" message={job.message} /> : null}
                  {job.issues?.length ? (
                    <Collapse
                      size="small"
                      items={[
                        {
                          key: "issues",
                          label: t("Show errors"),
                          children: (
                            <List
                              size="small"
                              dataSource={job.issues}
                              renderItem={issue => (
                                <List.Item>
                                  <span className="mat-wrap">
                                    {issue.file} · {t("line")} {issue.line}: {issue.message}
                                  </span>
                                </List.Item>
                              )}
                            />
                          )
                        }
                      ]}
                    />
                  ) : null}
                </div>
              </List.Item>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default ImportTab;
